import type { ResizeCallbackData } from 'react-resizable';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  faArrowUpRightFromSquare,
  faMobile,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonBase, Fab, Stack } from '@sixui/ui';
import { focusRingTokens } from '@sixui/ui/components/FocusRing/FocusRing.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { motionTokens } from '@sixui/ui/themes/base/motion.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { zIndexTokens } from '@sixui/ui/themes/base/zIndex.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { ResizableBox } from 'react-resizable';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISize } from '~/helpers/types';
import type { IAppPreviewRoute } from './AppPreviewState';
import { PhoneSkin } from '~/components/atoms/PhoneSkin';
import { getSizeFromRatio } from '~/helpers/getSizeFromRatio';
import { useAppRegion } from '~/hooks/useAppRegion';
import { useApi } from '~/lib/api';
import { appRegionsSettings } from '~/lib/appSettings';
import { useAppPreviewDispatch } from './useAppPreviewDispatch';
import { useAppPreviewState } from './useAppPreviewState';

export type IAppPreviewProps = IContainerProps & {
  thumb?: boolean;
};

type IEvent = {
  event: string;
  args: {
    state: string;
  };
};

export const APP_PREVIEW_TRAILING_SPACE = 48;
export const APP_PREVIEW_BOTTOM_SPACE = 48;
export const APP_PREVIEW_TOP_SPACE = 64;
export const APP_PREVIEW_BUTTON_GAP = 16;
export const APP_PREVIEW_BUTTON_HEIGHT = 56;

const RATIO = 408 / 831;
const INITIAL_MAX_HEIGHT_FACTOR = 0.75;

const MIN_WIDTH = 150;
const MIN_HEIGHT = MIN_WIDTH / RATIO;

const THUMB_WIDTH = 100;
const THUMB_HEIGHT = THUMB_WIDTH / RATIO;

const MAXIMUM_SIZE_EPSILON = 8;

const getScaleFromSize = (size: ISize): number => 0.002 * size.width + 0.018;

const styles = stylex.create({
  host: {
    position: 'fixed',
    right: APP_PREVIEW_TRAILING_SPACE,
    bottom: APP_PREVIEW_BOTTOM_SPACE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: APP_PREVIEW_BUTTON_GAP,
    zIndex: `calc(${zIndexTokens.modal} - 1)`,
    transitionProperty: 'opacity',
    transitionDuration: motionTokens.duration$short3,
    transitionTimingFunction: motionTokens.easing$emphasizedDecelerate,
  },
  host$invisible: {
    opacity: 0,
    pointerEvents: 'none',
  },
  skin: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  container: {
    position: 'relative',
    display: 'flex',
    width: THUMB_WIDTH,
    height: THUMB_HEIGHT,
    borderRadius: shapeTokens.corner$sm,

    // To align the right side of the phone skin with the right side of the
    // FAB button.
    marginRight: '-1.7%',
  },
  container$thumb: {
    backgroundColor: `color-mix(in srgb, ${colorSchemeTokens.surface}, transparent 40%)`,
  },
  resizing: {
    pointerEvents: 'none',
  },
  screen: {
    backgroundColor: colorSchemeTokens.surface,
    position: 'absolute',
    insetInline: '5%',
    insetBlock: '2%',
  },
  screen$text: {
    color: colorSchemeTokens.onSurfaceVariant,
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  screen$text$thumb: {
    backgroundColor: 'transparent',
  },
  iframe: (scale: number) => ({
    width: `${(1 + (1 - scale) / scale) * 100}%`,
    height: `${(1 + (1 - scale) / scale) * 100}%`,
    borderWidth: 0,
    scale,
    transformOrigin: '0 0',
    userSelect: 'none',
    backgroundColor: colorSchemeTokens.surface,
  }),
  handle: {
    position: 'absolute',
    cursor: 'col-resize',
    top: 0,
    bottom: 0,
    width: 20,
  },
  resizeGrip: {
    transform: 'rotate(180deg)',
  },
});

const buttonFocusRingStyles = stylex.create({
  host: {
    [focusRingTokens.shape]: shapeTokens.corner$xs,
    overflow: 'hidden',
  },
});

const getMaxHeight = (windowHeight: number): number =>
  windowHeight -
  (APP_PREVIEW_BOTTOM_SPACE +
    APP_PREVIEW_BUTTON_HEIGHT +
    APP_PREVIEW_BUTTON_GAP +
    APP_PREVIEW_TOP_SPACE);

export const AppPreview: React.FC<IAppPreviewProps> = (props) => {
  const { thumb } = props;
  const { t } = useTranslation();
  const api = useApi();
  const initialSize = getSizeFromRatio(
    { height: getMaxHeight(window.innerHeight) * INITIAL_MAX_HEIGHT_FACTOR },
    RATIO,
  );
  const { width: initialWidth, height: initialHeight } = initialSize;
  const location = useLocation();
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const state = useAppPreviewState();
  const dispatch = useAppPreviewDispatch();
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();
  const [canPost, setCanPost] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const windowSize = useWindowSize();
  const [isMaximized, setIsMaximized] = useState(false);
  const [maxSize, setMaxSize] = useState(initialSize);
  const [savedSize, setSavedSize] = useState<ISize | undefined>();
  const [resizing, setResizing] = useState(false);
  const appRegion = useAppRegion();

  const { route, url, opened, errorMessage } = state;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => setIsFooterVisible(entries[0].isIntersecting),
      {
        // threshold: FOOTER_VISIBILITY_THRESHOLD,
        rootMargin: `-${APP_PREVIEW_BOTTOM_SPACE}px`,
      },
    );
    const footer = document.getElementById('crowdaa-footer');
    if (footer) {
      observer.observe(footer);
    }

    return () => {
      observer.disconnect();
    };
  }, [location]);

  // Convenient non-undefined aliases for current size.
  const width = state.width ?? initialWidth;
  const height = state.height ?? initialHeight;

  const isMaxSizeReached =
    width >= maxSize.width - MAXIMUM_SIZE_EPSILON ||
    height >= maxSize.height - MAXIMUM_SIZE_EPSILON;

  const postNavigateToroute = useCallback((route: IAppPreviewRoute) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    iframeWindow?.postMessage(
      {
        action: 'navigate',
        url: route.path,
        tab: route.tab,
        crowdaa: true,
        navigateOptions: route.navigateOptions,
      },
      '*',
    );
  }, []);

  useEffect(() => {
    // When the route changes and the app preview is open, we update the URL of
    // the iframe to navigate to the new route.

    if (!opened || !route) {
      setIframeUrl(undefined);
      setCanPost(false);

      return;
    }

    if (appRegion) {
      const { previewUrl } = appRegionsSettings[appRegion];
      const url = `${previewUrl}/preview-generic/index.html?apiKey=appId:${route.appId}`;
      setIframeUrl(url);

      if (canPost) {
        postNavigateToroute(route);
      }
    }
  }, [opened, route, canPost, postNavigateToroute, iframeUrl, api, appRegion]);

  useEffect(() => {
    // Update the state of the ability to post messages to the iframe.

    if (!opened || !route) {
      dispatch.setIframeRef(undefined);
      setCanPost(false);

      return;
    }

    const handler = (message: MessageEvent<IEvent>): void => {
      if (
        message.data.event === 'appStateChange' &&
        message.data.args.state === 'externalNavigationReady'
      ) {
        dispatch.setIframeRef(iframeRef);
        setCanPost(true);
        postNavigateToroute(route);
      }
    };
    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
    };
  }, [opened, route, postNavigateToroute, dispatch]);

  useEffect(() => {
    // On the first render, the width and height of the app preview are
    // undefined so we set them to the initial values, which are calculated
    // based on the window size.

    if (state.width === undefined || state.height === undefined) {
      dispatch.setSize({
        width: initialWidth,
        height: initialHeight,
      });
    }
  }, [state.width, state.height, dispatch, initialWidth, initialHeight]);

  useEffect(() => {
    // When the window size changes, we need to recalculate the maximum size
    // that the app preview can have, and clamp the current size to the new
    // maximum size if it exceeds it.

    const maxHeight = getMaxHeight(windowSize.height);
    const maxSize = getSizeFromRatio({ height: maxHeight }, RATIO);
    setMaxSize(maxSize);

    const maxSizeExceeding = width > maxSize.width || height > maxSize.height;
    if (isMaximized || maxSizeExceeding) {
      dispatch.setSize(maxSize);

      if (maxSizeExceeding && !savedSize && !isMaxSizeReached) {
        setSavedSize({ width, height });
      }
    } else {
      const shouldRestoreSize =
        savedSize && (width < savedSize.width || height < savedSize.height);
      if (shouldRestoreSize) {
        // Restore the size of the app preview to the size before it exceeded the
        // maximum size.
        dispatch.setSize({
          width: Math.min(maxSize.width, savedSize.width),
          height: Math.min(maxSize.height, savedSize.height),
        });
      }
    }
  }, [
    windowSize,
    isMaximized,
    width,
    height,
    dispatch,
    savedSize,
    isMaxSizeReached,
  ]);

  const handleResize = (
    _: React.SyntheticEvent,
    { size }: ResizeCallbackData,
  ): void => {
    dispatch.setSize(size, { persistent: true });
  };

  const handleResizeStart = (): void => {
    setResizing(true);
    setIsMaximized(false);
    setSavedSize(undefined);
  };

  const handleResizeStop = (): void => {
    setResizing(false);

    // If the user resized the app preview to the maximum allowed size, the
    // app preview is considered maximized. It will be considered NOT maximized
    // when the user will resize it to a smaller size.
    setIsMaximized(isMaxSizeReached);

    // Only save state on resize when the action has been initiated by the user.
    dispatch.saveState();
  };

  if (state.hidden) {
    return null;
  }

  return (
    <div
      {...stylex.props(
        styles.host,
        isFooterVisible && styles.host$invisible,
        props.sx,
      )}
    >
      {opened ? (
        <ResizableBox
          {...stylex.props(styles.container)}
          width={width}
          height={height}
          onResize={handleResize}
          resizeHandles={['nw']}
          lockAspectRatio
          handle={<div {...stylex.props(styles.handle)} />}
          minConstraints={[MIN_WIDTH, MIN_HEIGHT]}
          maxConstraints={[maxSize.width, maxSize.height]}
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeStop}
        >
          <div {...stylex.props(resizing && styles.resizing)}>
            {errorMessage ? (
              <div {...stylex.props(styles.screen, styles.screen$text)}>
                {errorMessage}
              </div>
            ) : (
              <div {...stylex.props(styles.screen)}>
                <iframe
                  {...stylex.props(
                    styles.iframe(getScaleFromSize({ width, height })),
                  )}
                  ref={iframeRef}
                  title={t('AppPreview.app-preview')}
                  src={url ?? iframeUrl ?? 'about:blank'}
                ></iframe>
              </div>
            )}
            <PhoneSkin sx={styles.skin} />
          </div>
        </ResizableBox>
      ) : thumb ? (
        <ButtonBase
          sx={[styles.container, styles.container$thumb]}
          type="button"
          onClick={() => dispatch.setOpened(true, { persistent: true })}
          innerStyles={{
            focusRing: buttonFocusRingStyles,
          }}
        >
          <div
            {...stylex.props(
              styles.screen,
              styles.screen$text,
              styles.screen$text$thumb,
            )}
          >
            {t('AppPreview.click-to-open-preview')}
          </div>
          <PhoneSkin sx={styles.skin} />
        </ButtonBase>
      ) : null}

      <Stack horizontal gap={6} justify="end">
        {opened && route ? (
          <Fab
            variant="secondary"
            href={`/preview/${route.appId}.${appRegion}`}
            target="_blank"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </Fab>
        ) : null}

        <Fab
          variant="primary"
          label={
            thumb
              ? undefined
              : opened
                ? undefined
                : t('AppPreview.open-application-preview')
          }
          onClick={() => dispatch.setOpened(!opened, { persistent: true })}
        >
          <FontAwesomeIcon icon={opened ? faXmark : faMobile} />
        </Fab>
      </Stack>
    </div>
  );
};
